import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  RefreshControl,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import * as Clipboard from 'expo-clipboard';
import { RootStackParamList } from '../types';
import { adminAnalyticsService, AnalyticsData } from '../services/adminAnalytics';
import { buildTractionReport } from '../utils/tractionReport';
import { useDialog } from '../contexts';
import { Colors } from '../constants';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminAnalytics'>;

const fmt = (n: number | null | undefined, decimals = 0): string => {
  if (n == null) return '—';
  return n.toLocaleString('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const fmtEur = (n: number | null | undefined): string => {
  if (n == null) return '—';
  return `€\u202F${n.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const KpiCard: React.FC<{ label: string; value: string; icon: string; accent?: string }> = ({
  label, value, icon, accent = Colors.primary,
}) => (
  <View style={[styles.kpiCard, { borderTopColor: accent }]}>
    <Text style={styles.kpiIcon}>{icon}</Text>
    <Text style={styles.kpiValue}>{value}</Text>
    <Text style={styles.kpiLabel}>{label}</Text>
  </View>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>{title}</Text>
    <View style={styles.sectionCard}>{children}</View>
  </View>
);

const Row: React.FC<{ label: string; value: string; sub?: string; indent?: boolean }> = ({
  label, value, sub, indent,
}) => (
  <View style={[styles.row, indent && styles.rowIndent]}>
    <View style={styles.rowLeft}>
      <Text style={styles.rowLabel}>{label}</Text>
      {sub ? <Text style={styles.rowSub}>{sub}</Text> : null}
    </View>
    <Text style={styles.rowValue}>{value}</Text>
  </View>
);

const Divider = () => <View style={styles.divider} />;

// ─── Main screen ─────────────────────────────────────────────────────────────

export const AdminAnalyticsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (isRefresh = false) => {
    try {
      const result = await adminAnalyticsService.getAnalytics();
      setData(result);
    } catch (err: any) {
      dialog.alert(t('common.errors.error'), err.message || t('common.errors.loadFailed'), 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onRefresh = () => { setRefreshing(true); load(true); };

  const handleCopyReport = async () => {
    if (!data) return;
    const dateLabel = new Date().toLocaleDateString('nl-NL', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
    try {
      await Clipboard.setStringAsync(buildTractionReport(data, dateLabel));
      dialog.alert(t('common.success'), t('screens.adminAnalytics.reportCopied'), 'success');
    } catch {
      dialog.alert(t('common.errors.error'), t('common.errors.operationFailed'), 'error');
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        {renderHeader(navigation, t)}
        <View style={styles.center}>
          <ActivityIndicator size="large" color={Colors.primary} />
        </View>
      </View>
    );
  }

  if (!data) return null;

  const { users, items, transactions, clubs, engagement, moderation, funnel, traction } = data;

  // Conversion vs the previous funnel step
  const pctOf = (part: number, whole: number): string =>
    whole > 0 ? `${fmt((part / whole) * 100, 1)}%` : '—';

  const fmtWeek = (isoDate: string): string => {
    // week_start is a date-only string; new Date('YYYY-MM-DD') parses as UTC and
    // can show the previous day in some timezones. Parse as a local calendar date.
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
  };

  const categoryLabels: Record<string, string> = {
    jersey: 'Shirt', shorts: 'Short', socks: 'Sokken',
    jacket: 'Jas', other: 'Overig',
  };

  return (
    <View style={styles.container}>
      {renderHeader(navigation, t)}
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={Colors.primary} />}
      >
        <View style={styles.inner}>

          {/* ── Deelbaar rapport ── */}
          <TouchableOpacity style={styles.copyReportButton} onPress={handleCopyReport}>
            <Ionicons name="copy-outline" size={18} color={Colors.primary} />
            <Text style={styles.copyReportText}>{t('screens.adminAnalytics.copyReport')}</Text>
          </TouchableOpacity>

          {/* ── KPI row ── */}
          <View style={styles.kpiRow}>
            <KpiCard label={t('screens.adminAnalytics.kpiUsers')} value={fmt(users.total)} icon="👤" accent="#2D6A4F" />
            <KpiCard label={t('screens.adminAnalytics.kpiListings')} value={fmt(items.total)} icon="🏷️" accent={Colors.primary} />
            <KpiCard label={t('screens.adminAnalytics.kpiTransactions')} value={fmt(transactions.total)} icon="💳" accent="#5B8FD4" />
            <KpiCard label={t('screens.adminAnalytics.kpiGmv30d')} value={fmtEur(traction.gmv_30d)} icon="💰" accent="#F2994A" />
            <KpiCard label={t('screens.adminAnalytics.kpiGmv')} value={fmtEur(traction.gmv_total)} icon="🤝" accent="#9B59B6" />
            <KpiCard
              label={t('screens.adminAnalytics.kpiOpenReports')}
              value={fmt(moderation.open_backlog)}
              icon="🚨"
              accent={moderation.open_backlog > 0 ? '#E53E3E' : '#68D391'}
            />
          </View>

          {/* ── Funnel: listing → bekeken → chat → verkocht ── */}
          <Section title={`🔻  ${t('screens.adminAnalytics.funnel')}`}>
            <Row label={t('screens.adminAnalytics.funnelListings')} value={fmt(funnel.listings)} />
            <Row
              label={t('screens.adminAnalytics.funnelViewed')}
              value={fmt(funnel.items_viewed)}
              sub={t('screens.adminAnalytics.ofPrevious', { pct: pctOf(funnel.items_viewed, funnel.listings) })}
              indent
            />
            <Row
              label={t('screens.adminAnalytics.funnelChats')}
              value={fmt(funnel.chats_started)}
              sub={t('screens.adminAnalytics.ofPrevious', { pct: pctOf(funnel.chats_started, funnel.items_viewed) })}
              indent
            />
            <Row
              label={t('screens.adminAnalytics.funnelDeals')}
              value={fmt(funnel.deals_completed)}
              sub={t('screens.adminAnalytics.ofPrevious', { pct: pctOf(funnel.deals_completed, funnel.chats_started) })}
              indent
            />
            <Divider />
            <Text style={styles.subheading}>{t('screens.adminAnalytics.last30days')}</Text>
            <Row label={t('screens.adminAnalytics.funnelListings')} value={fmt(funnel.listings_30d)} indent />
            <Row label={t('screens.adminAnalytics.funnelViewed')} value={fmt(funnel.items_viewed_30d)} indent />
            <Row label={t('screens.adminAnalytics.funnelChats')} value={fmt(funnel.chats_started_30d)} indent />
            <Row label={t('screens.adminAnalytics.funnelDeals')} value={fmt(funnel.deals_completed_30d)} indent />
          </Section>

          {/* ── Tractie: het groeiverhaal voor externe partijen ── */}
          <Section title={`🚀  ${t('screens.adminAnalytics.traction')}`}>
            <Row
              label={t('screens.adminAnalytics.gmvTotal')}
              value={fmtEur(traction.gmv_total)}
              sub={t('screens.adminAnalytics.gmvSub')}
            />
            <Row label={t('screens.adminAnalytics.gmv30d')} value={fmtEur(traction.gmv_30d)} indent />
            <Row
              label={t('screens.adminAnalytics.gmvCoverage')}
              value={`${fmt(traction.gmv_price_coverage_pct, 1)}%`}
              sub={t('screens.adminAnalytics.gmvCoverageSub')}
              indent
            />
            <Divider />
            <Row
              label={t('screens.adminAnalytics.dealsOnPlatform')}
              value={fmt(traction.deals_on_platform)}
              sub={t('screens.adminAnalytics.dealsOnPlatformSub')}
            />
            <Row
              label={t('screens.adminAnalytics.dealsOffPlatform')}
              value={fmt(traction.deals_off_platform)}
              indent
            />
            <Divider />
            <Row
              label={t('screens.adminAnalytics.medianDaysToSale')}
              value={traction.median_days_to_sale != null
                ? `${fmt(traction.median_days_to_sale, 1)} ${t('screens.adminAnalytics.days')}`
                : '—'}
            />
            <Row
              label={t('screens.adminAnalytics.pctSoldWithin30d')}
              value={`${fmt(traction.pct_sold_within_30d, 1)}%`}
              sub={t('screens.adminAnalytics.pctSoldWithin30dSub')}
            />
            <Divider />
            <Row label={t('screens.adminAnalytics.repeatSellers')} value={fmt(traction.repeat_sellers)} />
            <Row label={t('screens.adminAnalytics.repeatBuyers')} value={fmt(traction.repeat_buyers)} />
            {traction.weekly.length > 0 && (
              <>
                <Divider />
                <Text style={styles.subheading}>{t('screens.adminAnalytics.weeklyTitle')}</Text>
                <View style={styles.weekRow}>
                  <Text style={[styles.weekCell, styles.weekCellWide, styles.weekHeaderText]}>{t('screens.adminAnalytics.weekCol')}</Text>
                  <Text style={[styles.weekCell, styles.weekHeaderText]}>{t('screens.adminAnalytics.signupsCol')}</Text>
                  <Text style={[styles.weekCell, styles.weekHeaderText]}>{t('screens.adminAnalytics.listingsCol')}</Text>
                  <Text style={[styles.weekCell, styles.weekHeaderText]}>{t('screens.adminAnalytics.dealsCol')}</Text>
                  <Text style={[styles.weekCell, styles.weekHeaderText]}>{t('screens.adminAnalytics.chattersCol')}</Text>
                </View>
                {traction.weekly.map((w) => (
                  <View key={w.week_start} style={styles.weekRow}>
                    <Text style={[styles.weekCell, styles.weekCellWide]}>{fmtWeek(w.week_start)}</Text>
                    <Text style={styles.weekCell}>{fmt(w.signups)}</Text>
                    <Text style={styles.weekCell}>{fmt(w.new_listings)}</Text>
                    <Text style={styles.weekCell}>{fmt(w.deals_completed)}</Text>
                    <Text style={styles.weekCell}>{fmt(w.active_chatters)}</Text>
                  </View>
                ))}
              </>
            )}
          </Section>

          {/* ── Sectie 1: Gebruikers ── */}
          <Section title={`👤  ${t('screens.adminAnalytics.users')}`}>
            <Row label={t('screens.adminAnalytics.totalRegistered')} value={fmt(users.total)} />
            <Divider />
            <Row label={t('screens.adminAnalytics.new')} value={fmt(users.new_7d)} sub={t('screens.adminAnalytics.last7days')} indent />
            <Row label={t('screens.adminAnalytics.new')} value={fmt(users.new_30d)} sub={t('screens.adminAnalytics.last30days')} indent />
            <Divider />
            <Row label={t('screens.adminAnalytics.active')} value={fmt(users.active_7d)} sub={t('screens.adminAnalytics.last7days')} indent />
            <Row label={t('screens.adminAnalytics.active')} value={fmt(users.active_30d)} sub={t('screens.adminAnalytics.last30days')} indent />
            <Divider />
            <Row label={t('screens.adminAnalytics.onboardingCompleted')} value={`${fmt(users.onboarding_done)} (${fmt(users.total > 0 ? Math.round(users.onboarding_done / users.total * 100) : 0)}%)`} />
          </Section>

          {/* ── Sectie 2: Advertenties ── */}
          <Section title={`🏷️  ${t('screens.adminAnalytics.listings')}`}>
            <Row label={t('screens.adminAnalytics.total')} value={fmt(items.total)} />
            <Divider />
            <Row label={t('screens.adminAnalytics.available')} value={fmt(items.available)} indent />
            <Row label={t('screens.adminAnalytics.reserved')}  value={fmt(items.reserved)} indent />
            <Row label={t('screens.adminAnalytics.sold')}       value={fmt(items.sold)} indent />
            <Divider />
            <Row label={t('screens.adminAnalytics.new7d')}  value={fmt(items.new_7d)} />
            <Row label={t('screens.adminAnalytics.new30d')} value={fmt(items.new_30d)} />
            <Row label={t('screens.adminAnalytics.averagePrice')} value={fmtEur(items.avg_price)} />
            {items.top_categories.length > 0 && (
              <>
                <Divider />
                <Text style={styles.subheading}>{t('screens.adminAnalytics.topCategories')}</Text>
                {items.top_categories.map((c, i) => (
                  <Row
                    key={c.category}
                    label={`${i + 1}. ${categoryLabels[c.category] ?? c.category}`}
                    value={fmt(c.item_count)}
                    indent
                  />
                ))}
              </>
            )}
          </Section>

          {/* ── Sectie 3: Transacties ── */}
          <Section title={`💳  ${t('screens.adminAnalytics.transactions')}`}>
            <Row label={t('screens.adminAnalytics.total')} value={fmt(transactions.total)} />
            <Divider />
            <Row label={t('screens.adminAnalytics.pending')} value={fmt(transactions.pending)} indent />
            <Row label={t('screens.adminAnalytics.paid')}               value={fmt(transactions.paid)} indent />
            <Row label={t('screens.adminAnalytics.completed')}              value={fmt(transactions.completed)} indent />
            <Row label={t('screens.adminAnalytics.disputed')}            value={fmt(transactions.disputed)} indent />
            <Row label={t('screens.adminAnalytics.cancelled')}           value={fmt(transactions.cancelled)} indent />
            <Divider />
            <Row label={t('screens.adminAnalytics.avgTransactionValue')} value={fmtEur(transactions.avg_deal_value)} />
          </Section>

          {/* ── Sectie 4: Clubs ── */}
          <Section title={`🏟️  ${t('screens.adminAnalytics.clubs')}`}>
            <Row label={t('screens.adminAnalytics.activeClubs')} value={fmt(clubs.total_active)} />
            {clubs.top_by_members.length > 0 && (
              <>
                <Divider />
                <Text style={styles.subheading}>{t('screens.adminAnalytics.topByMembers')}</Text>
                {clubs.top_by_members.map((c, i) => (
                  <Row key={c.name} label={`${i + 1}. ${c.name}`} value={`${fmt(c.member_count)} ${t('screens.adminAnalytics.members')}`} indent />
                ))}
              </>
            )}
            {clubs.top_by_listings.length > 0 && (
              <>
                <Divider />
                <Text style={styles.subheading}>{t('screens.adminAnalytics.topByListings')}</Text>
                {clubs.top_by_listings.map((c, i) => (
                  <Row key={c.name} label={`${i + 1}. ${c.name}`} value={`${fmt(c.listing_count)} ${t('screens.adminAnalytics.ads')}`} indent />
                ))}
              </>
            )}
          </Section>

          {/* ── Sectie 5: Engagement & Retentie ── */}
          <Section title={`📈  ${t('screens.adminAnalytics.engagement')}`}>
            <Row
              label={t('screens.adminAnalytics.conversionRate')}
              value={`${fmt(engagement.conversion_rate, 1)}%`}
              sub={t('screens.adminAnalytics.conversionRateSub')}
            />
            <Divider />
            <Row label={t('screens.adminAnalytics.messages7d')}  value={fmt(engagement.messages_7d)} />
            <Row label={t('screens.adminAnalytics.messages30d')} value={fmt(engagement.messages_30d)} />
            <Row
              label={t('screens.adminAnalytics.avgMessagesPerDay')}
              value={fmt(Math.round(engagement.messages_30d / 30))}
            />
            <Divider />
            <Row
              label={t('screens.adminAnalytics.retentionSeller')}
              value={`${fmt(engagement.retention_seller, 1)}%`}
              sub={t('screens.adminAnalytics.retentionSellerSub')}
            />
            <Row
              label={t('screens.adminAnalytics.retentionBuyer')}
              value={`${fmt(engagement.retention_buyer, 1)}%`}
              sub={t('screens.adminAnalytics.retentionBuyerSub')}
            />
          </Section>

          {/* ── Sectie 6: Moderatie ── */}
          <Section title={`🛡️  ${t('screens.adminAnalytics.moderation')}`}>
            <Row
              label={t('screens.adminAnalytics.openReports')}
              value={fmt(moderation.open_backlog)}
              sub={t('screens.adminAnalytics.openReportsSub')}
            />
            <Divider />
            <Row label={t('screens.adminAnalytics.totalHandled')} value={fmt(moderation.total_handled)} />
            <Row label={t('screens.adminAnalytics.approved')}         value={fmt(moderation.approved)} indent />
            <Row label={t('screens.adminAnalytics.deleted')}          value={fmt(moderation.deleted)} indent />
            <Divider />
            <Row
              label={t('screens.adminAnalytics.avgResolutionTime')}
              value={moderation.avg_resolution_hours != null
                ? `${fmt(moderation.avg_resolution_hours, 1)} ${t('screens.adminAnalytics.hours')}`
                : '—'}
              sub={t('screens.adminAnalytics.avgResolutionTimeSub')}
            />
          </Section>

        </View>
      </ScrollView>
    </View>
  );
};

function renderHeader(navigation: Props['navigation'], t: (k: string) => string) {
  return (
    <View style={[styles.header, Platform.OS === 'web' && styles.headerWeb]}>
      <View style={styles.headerContent}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard')}>
          <Ionicons name="arrow-back" size={24} color={Colors.primary} />
          <Text style={styles.backText}>{t('common.buttons.back')}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Analyse</Text>
        <Text style={styles.headerSub}>Platform statistieken</Text>
      </View>
    </View>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  center:    { flex: 1, justifyContent: 'center', alignItems: 'center' },

  header: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    paddingTop: Platform.OS === 'web' ? 16 : 50,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  headerWeb:    { alignItems: 'center' },
  headerContent:{ width: '100%', maxWidth: 1200 },
  backButton:   { flexDirection: 'row', alignItems: 'center', marginBottom: 8, gap: 6 },
  backText:     { fontSize: 16, color: Colors.primary },
  headerTitle:  { fontSize: 24, fontWeight: '700', color: '#1A1A1A' },
  headerSub:    { fontSize: 14, color: '#666666', marginTop: 2 },

  scroll:        { flex: 1 },
  scrollContent: { ...(Platform.OS === 'web' ? { alignItems: 'center' as const } : {}) },
  inner: {
    width: '100%',
    padding: 16,
    gap: 16,
    ...(Platform.OS === 'web' ? { maxWidth: 1200 } : {}),
  },

  // KPI row
  kpiRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  kpiCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    padding: 14,
    borderTopWidth: 3,
    minWidth: 130,
    flex: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.07,
    shadowRadius: 4,
    elevation: 2,
  },
  kpiIcon:  { fontSize: 22, marginBottom: 6 },
  kpiValue: { fontSize: 22, fontWeight: '700', color: '#1A1A1A', marginBottom: 2 },
  kpiLabel: { fontSize: 12, color: '#666666' },

  // Sections
  section:     { gap: 8 },
  sectionTitle:{ fontSize: 16, fontWeight: '700', color: '#1A1A1A', paddingLeft: 2 },
  sectionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingVertical: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.07,
    shadowRadius: 4,
    elevation: 2,
  },
  subheading: {
    fontSize: 12,
    fontWeight: '600',
    color: '#999999',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 4,
  },

  // Rows
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 11,
    gap: 8,
  },
  rowIndent: { paddingLeft: 28 },
  rowLeft:   { flex: 1 },
  rowLabel:  { fontSize: 15, color: '#1A1A1A' },
  rowSub:    { fontSize: 12, color: '#999999', marginTop: 1 },
  rowValue:  { fontSize: 15, fontWeight: '600', color: '#1A1A1A', textAlign: 'right' },
  divider:   { height: 1, backgroundColor: '#F0F0F0', marginHorizontal: 16 },

  // Weekly growth table
  weekRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 7,
    gap: 4,
  },
  weekCell:       { flex: 1, fontSize: 13, color: '#1A1A1A', textAlign: 'right' },
  weekCellWide:   { flex: 1.4, textAlign: 'left' },
  weekHeaderText: { fontSize: 12, fontWeight: '600', color: '#999999' },

  copyReportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.primary,
    paddingVertical: 12,
  },
  copyReportText: { fontSize: 15, fontWeight: '600', color: Colors.primary },
});
